import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";

import {
  Table,
  multiSelect,
  SelectList,
} from "../../../../components/Functional";
import { getInput } from "../../../../components/ui/Input";
import { FETCH_LISTS as fetchLists } from "../../../../graphql/query/List";

import Actions from "./components/Actions";
import Header from "../../../../components/ui/ListHeader";
import SearchedReservations from "./components/SearchedReservations";

import * as SelectListWorking from "./components/SelectListWorking";
import * as SelectListSaved from "./components/SelectListSaved";
import {
  reservationFilterStore,
  reservationNavigationStore,
} from "../../components/useReservationFilters";
import { updateSearchResultNavigationStore } from "../../../../util/searchResultNavigation";

const RESERVATION_CATEGORY_OPTIONS = [
  { label: "All", value: "" },
  { label: "Reservation", value: 1 },
  { label: "Make Available", value: 2 },
];

const List = props => {
  const { formik } = props;
  const navigate = useNavigate();

  useSyncExternalStore(
    reservationFilterStore.subscribe,
    reservationFilterStore.get,
    reservationFilterStore.get,
  );

  const handleCategoryFilterChange = newValue => {
    const current = reservationFilterStore.get();
    const next = { ...current };
    if (newValue === "") {
      delete next.categoryId;
    } else {
      next.categoryId = { value: newValue, operator: "eq" };
    }
    reservationFilterStore.set(next);
  };

  const { data: { lists: { edges = [] } = {} } = {} } = useQuery(fetchLists(), {
    variables: {
      // fetch Reservation_working list
      input: { typeId: 26 },
    },
    fetchPolicy: "network-only",
  });
  const workingList = edges?.length > 0 ? edges[0] : null;
  const workingListId = workingList?.id;
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams?.get("status") || "all";
  const listId = +queryParams.get("listId");
  const ids = Object.keys(formik?.values?.selected?.ids || {})?.map(item =>
    parseInt(item.replace("_", "")),
  );

  useEffect(() => {
    if (workingListId) {
      queryParams.set("workingListId", workingListId);
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      // eslint-disable-next-line
      history.pushState(null, null, newUrl);
    }
  }, [workingListId]);

  const multiSelectProps = {
    formik,
    label: "Reservations",
    name: "selected",
    keyName: "id",
  };

  const actionProps = {
    formik,
    ids,
    listId,
    onSuccess: () => formik?.resetForm(),
    search: reservationFilterStore.get(),
    workingListId,
  };

  const captureNavigationSnapshot = useCallback(
    ({ count, edges, queryVariables }) => {
      if (status !== "all") return;

      const currentQueryParams = new URLSearchParams(window.location.search);
      const currentListId = currentQueryParams.get("listId");
      const currentWorkingListId = currentQueryParams.get("workingListId");

      updateSearchResultNavigationStore(reservationNavigationStore, {
        count,
        edges,
        queryVariables,
        statusContext: "all",
        listContext: {
          ...(currentListId ? { listId: currentListId } : {}),
          ...(currentWorkingListId
            ? { workingListId: currentWorkingListId }
            : {}),
        },
      });
    },
    [status],
  );

  const handleChange = value => {
    formik?.resetForm();
    let url = `/reservations?status=${value}`;

    // For working list tabs, use listId if present (saved list), otherwise use workingListId
    if (value === "selectListWorking" || value === "selectListSaved") {
      // Include listId if present (viewing/editing a saved list), otherwise use workingListId
      if (listId) {
        url += `&listId=${listId}`;
      } else if (workingListId) {
        url += `&workingListId=${workingListId}`;
      }
    } else {
      // For "all" and other tabs, preserve saved list IDs (listId) so items can be added to them
      // But don't include working list IDs
      if (listId) {
        url += `&listId=${listId}`;
      }
    }

    navigate(url);
  };

  const GeneralTabActions = () => {
    const createListProps = {
      ...actionProps,
      onSuccess: () => (
        formik?.resetForm(),
        navigate(
          `/reservations?status=selectListWorking${listId ? `&listId=${listId}` : ""}`,
        )
      ),
      typeId: 26,
    };

    return (
      <>
        <SelectList.CreateList {...createListProps} />
      </>
    );
  };

  const tabProps = {
    active: status,
    onChange: handleChange,
    tabs: [
      {
        actions: GeneralTabActions,
        label: "All Reservations",
        name: "all",
        tableProps: SearchedReservations(),
      },
      {
        actions: SelectListWorking.Actions,
        label: "Select List (Working)",
        name: "selectListWorking",
        tableProps: SelectListWorking.tableProps(actionProps),
      },
      {
        actions: SelectListSaved.Actions,
        label: "Select List (Saved)",
        name: "selectListSaved",
        tableProps: SelectListSaved.tableProps(actionProps),
      },
    ],
  };

  const { tableProps: tabTableProps, actions: tabActions } =
    tabProps?.tabs?.find(tab => tab.name === status) || {};

  // function fixes header.name for select lists when status = selectListWorking
  const fixHeadersName = ({ status, headers }) =>
    headers?.map(header => {
      return {
        ...header,
        name:
          header?.name && status === "selectListWorking"
            ? Array.isArray(header?.name)
              ? header?.name?.map(item => `item.${item}`)
              : `item.${header?.name}`
            : header?.name,
      };
    });

  const ReservationCategory = ({ values }) => {
    const { categoryId } = values;
    return <div>{categoryId === 2 ? "Make Available" : "Reservation"}</div>;
  };

  const tableProps = {
    headers: fixHeadersName({
      status,
      headers: [
        multiSelect(multiSelectProps),
        {
          label: "ID",
          name: "id",
          rowClass: "box-border",
          type: "label",
        },
        {
          label: "Category",
          name: "categoryId",
          type: "custom",
          component: ReservationCategory,
        },
        {
          label: "Type",
          name: "type.description",
          rowClass: "min-w-[320px] box-border",
          type: "label",
        },
        {
          label: "Date",
          name: "date",
          type: "date",
        },
        {
          label: "End of Reservation",
          name: "dateDue",
          type: "date",
        },
        {
          label: "Reserved For",
          name: "reservationUser.name",
          type: "label",
        },
        {
          label: "Created By",
          name: "createdBy.name",
          type: "label",
        },
        {
          label: "# of Works",
          name: "reservationInstanceCount",
          type: "label",
        },
        { label: "", type: "custom", component: Actions },
      ],
    }),
    onQueryStateChange: captureNavigationSnapshot,
    tabProps,
    variant: "dynamicHeight",
    ...tabTableProps,
  };

  const currentCategoryFilter =
    reservationFilterStore.get()?.categoryId?.value ?? "";

  return (
    <>
      <Header
        leftText="All Reservations"
        rightComponent={() => {
          return (
            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
              {status === "all" && (
                <div className="w-full sm:w-auto sm:min-w-[180px]">
                  {getInput({
                    label: "",
                    name: "categoryId",
                    type: "select",
                    options: RESERVATION_CATEGORY_OPTIONS,
                    formik: {
                      values: { categoryId: currentCategoryFilter },
                      setFieldValue: (name, value) =>
                        handleCategoryFilterChange(value),
                      getFieldProps: () => ({
                        value: currentCategoryFilter,
                      }),
                      getFieldHelpers: () => ({
                        setValue: value => handleCategoryFilterChange(value),
                      }),
                    },
                  })}
                </div>
              )}
              {tabActions?.(actionProps)}
            </div>
          );
        }}
      />
      <div className="mt-6 w-full overflow-x-auto">
        <Table {...tableProps} />
      </div>
    </>
  );
};

export default List;
